import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import {
  EMAIL_DOMAIN,
  isValidLocalPart,
  sanitizeLocalPart,
} from '@/lib/cloud';
import type { CloudEmail } from '@/lib/cloud';

const BUCKET = 'media';

const DOC_TYPES = [
  { value: 'carte_electeur', label: "Carte d'électeur" },
  { value: 'cni', label: 'CNI' },
  { value: 'passport', label: 'Passeport' },
];

const COUNTRIES = [
  'Togo',
  'Bénin',
  'Burkina Faso',
  'Côte d’Ivoire',
  'Ghana',
  'Mali',
  'Niger',
  'Nigeria',
  'Sénégal',
  'Cameroun',
  'Gabon',
  'Congo',
  'France',
  'Belgique',
  'Canada',
  'États-Unis',
  'Allemagne',
  'Autre',
];

function verificationLabel(status: string | null, type: string | null) {
  if (type === 'professional') {
    if (status === 'verified') return { text: 'Vérifié', cls: 'bg-green-500/15 text-green-600' };
    if (status === 'pending') return { text: 'Vérification en attente', cls: 'bg-amber-500/15 text-amber-600' };
    return { text: 'Non vérifié', cls: 'bg-foreground-200/70 text-foreground-400' };
  }
  return { text: 'Personnel', cls: 'bg-secondary-100 text-secondary-900' };
}

function EmailSection() {
  const { user, profile } = useAuth();
  const iconInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);

  const [email, setEmail] = useState<CloudEmail | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const [type, setType] = useState<'personal' | 'professional'>('personal');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [country, setCountry] = useState('');
  const [phone, setPhone] = useState('');
  const [localPart, setLocalPart] = useState('');
  const [iconUrl, setIconUrl] = useState('');
  const [iconUploading, setIconUploading] = useState(false);
  const [docType, setDocType] = useState('carte_electeur');
  const [docUrl, setDocUrl] = useState('');
  const [docUploading, setDocUploading] = useState(false);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    let active = true;
    (async () => {
      try {
        const { data } = await supabase
          .from('cloud_email_addresses')
          .select('*')
          .eq('owner_id', user.id)
          .maybeSingle();
        if (active && data) setEmail(data as CloudEmail);
      } catch {
        if (active) setError("Impossible de charger votre adresse email.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [user]);

  useEffect(() => {
    if (profile?.full_name) {
      const parts = profile.full_name.trim().split(/\s+/);
      if (parts.length >= 2) {
        setFirstName(parts[0]);
        setLastName(parts.slice(1).join(' '));
      } else if (parts.length === 1) {
        setFirstName(parts[0]);
      }
    }
    if (profile?.country) setCountry(profile.country);
    if (profile?.phone) setPhone(profile.phone);
  }, [profile]);

  const handleIconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!user || !file) return;
    setError(null);
    if (!file.type.startsWith('image/')) {
      setError("Veuillez choisir une image pour l'icône.");
      return;
    }
    setIconUploading(true);
    try {
      const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
      const path = `cloud/${user.id}/icons/${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}.${ext}`;
      const { error: upError } = await supabase.storage
        .from(BUCKET)
        .upload(path, file, { cacheControl: '3600', upsert: false });
      if (upError) throw upError;
      const { data: pub } = supabase.storage.from(BUCKET).getPublicUrl(path);
      setIconUrl(pub.publicUrl);
    } catch {
      setError("Impossible de téléverser l'icône.");
    } finally {
      setIconUploading(false);
      if (iconInputRef.current) iconInputRef.current.value = '';
    }
  };

  const handleDocUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!user || !file) return;
    setError(null);
    setDocUploading(true);
    try {
      const ext = file.name.split('.').pop()?.toLowerCase() || 'bin';
      const path = `cloud/${user.id}/verification/${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}.${ext}`;
      const { error: upError } = await supabase.storage
        .from(BUCKET)
        .upload(path, file, { cacheControl: '3600', upsert: false });
      if (upError) throw upError;
      const { data: pub } = supabase.storage.from(BUCKET).getPublicUrl(path);
      setDocUrl(pub.publicUrl);
    } catch {
      setError("Impossible de téléverser la pièce d'identité.");
    } finally {
      setDocUploading(false);
      if (docInputRef.current) docInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user) return;
    setError(null);
    setSuccess(null);

    const local = sanitizeLocalPart(localPart);
    if (!isValidLocalPart(local)) {
      setError(
        "Adresse invalide. Utilisez 3 à 30 caractères (lettres, chiffres, point, tiret, underscore), sans commencer ni finir par un symbole."
      );
      return;
    }
    if (!firstName.trim() || !lastName.trim() || !country.trim() || !phone.trim()) {
      setError(
        "Veuillez renseigner votre nom, prénom, pays et numéro de téléphone pour créer votre adresse."
      );
      return;
    }
    if (type === 'professional' && !docUrl) {
      setError(
        "Pour une adresse professionnelle, veuillez téléverser une pièce d'identité (carte d'électeur, CNI ou passeport)."
      );
      return;
    }

    setSubmitting(true);
    try {
      const fullEmail = `${local}@${EMAIL_DOMAIN}`;
      const { data, error: insertError } = await supabase
        .from('cloud_email_addresses')
        .insert({
          owner_id: user.id,
          local_part: local,
          email: fullEmail,
          status: 'active',
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          country: country.trim(),
          phone: phone.trim(),
          type,
          verification_status: type === 'professional' ? 'pending' : 'none',
          verification_doc_url: docUrl || null,
          verification_doc_type: type === 'professional' ? docType : null,
          icon_url: iconUrl || null,
        })
        .select()
        .maybeSingle();

      if (insertError) {
        if (insertError.message?.toLowerCase().includes('duplicate')) {
          setError("Cette adresse est déjà prise. Choisissez-en une autre.");
        } else {
          setError(insertError.message || "Une erreur est survenue.");
        }
        return;
      }

      setEmail(data as CloudEmail);
      setLocalPart('');
      setSuccess("Votre adresse email a été créée avec succès !");

      // Email de confirmation à l'utilisateur
      if (user?.email) {
        try {
          await supabase.functions.invoke('send-email', {
            body: {
              to: user.email,
              subject: 'Votre adresse WorldApeiron Cloud est créée',
              html: `<p>Bonjour ${firstName.trim()},</p><p>Votre adresse <strong>${fullEmail}</strong> a été créée avec succès sur WorldApeiron Cloud.</p>${type === 'professional' ? "<p>En tant qu'adresse professionnelle, elle est en attente de vérification. Notre équipe examinera votre pièce d'identité sous peu.</p>" : '<p>Vous pouvez dès à présent l\'utiliser dans l\'écosystème.</p>'}`,
            },
          });
        } catch {
          // Silencieux — l'email de confirmation est non critique
        }
      }

      // Notification admin pour vérification professionnelle
      if (type === 'professional') {
        try {
          await supabase.functions.invoke('send-email', {
            body: {
              to: 'admin@worldapeiron.com',
              subject: `Nouvelle vérification d'identité : ${fullEmail}`,
              html: `<h2 style="color:#111;font-family:sans-serif;">Nouvelle demande de vérification d'identité</h2>
<p><strong>Nom :</strong> ${firstName.trim()} ${lastName.trim()}</p>
<p><strong>Adresse demandée :</strong> ${fullEmail}</p>
<p><strong>Pays :</strong> ${country.trim()}</p>
<p><strong>Téléphone :</strong> ${phone.trim()}</p>
<p><strong>Document soumis :</strong> ${docType}</p>
<p><a href="${docUrl}" target="_blank" style="color:#2563eb;">Voir le document téléversé</a></p>`,
            },
          });
        } catch {
          // Silencieux — la notification admin est non critique
        }
      }
    } catch {
      setError("Une erreur est survenue lors de la création.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!email) return;
    try {
      await navigator.clipboard.writeText(email.email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Impossible de copier l'adresse.");
    }
  };

  const handleDelete = async () => {
    if (!email) return;
    setError(null);
    setSuccess(null);
    try {
      const { error: delError } = await supabase
        .from('cloud_email_addresses')
        .delete()
        .eq('id', email.id);
      if (delError) {
        setError(delError.message || "Impossible de supprimer l'adresse.");
        return;
      }
      setEmail(null);
      setSuccess("Adresse email supprimée.");
    } catch {
      setError("Une erreur est survenue lors de la suppression.");
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[180px]">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-500/10">
          <i className="ri-mail-send-line text-xl text-primary-400" />
        </div>
        <div>
          <h3 className="font-heading font-semibold text-lg text-foreground-50">
            Mon adresse email
          </h3>
          <p className="text-xs text-foreground-400">
            Votre adresse personnalisée @{EMAIL_DOMAIN}
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-accent-100/70 text-accent-900 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 rounded-lg bg-primary-100 text-primary-900 text-sm">
          {success}
        </div>
      )}

      {email ? (
        <div className="p-5 rounded-xl bg-background-100 border border-background-200/70">
          <div className="flex items-start gap-4 flex-wrap">
            {email.icon_url ? (
              <img
                src={email.icon_url}
                alt={email.email}
                className="w-14 h-14 rounded-full object-cover border border-background-200/70 shrink-0"
              />
            ) : (
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-500/10 shrink-0">
                <i className="ri-user-line text-2xl text-primary-400" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <p className="font-heading font-bold text-xl text-foreground-50 break-all">
                  {email.email}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold bg-primary-500/10 text-primary-400">
                  {email.type === 'professional' ? 'Professionnel' : 'Personnel'}
                </span>
                {(() => {
                  const v = verificationLabel(email.verification_status, email.type);
                  return (
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${v.cls}`}
                    >
                      {v.text}
                      {email.verification_status === 'verified' && (
                        <i className="ri-shield-check-line" />
                      )}
                      {email.verification_status === 'pending' && (
                        <i className="ri-time-line" />
                      )}
                    </span>
                  );
                })()}
              </div>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-foreground-400">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line" />
                  {email.first_name} {email.last_name}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-flag-line" />
                  {email.country}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-phone-line" />
                  {email.phone}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={handleCopy}
              className="px-4 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors whitespace-nowrap text-sm"
            >
              {copied ? 'Copié !' : 'Copier'}
            </button>
          </div>

          {email.type === 'professional' && email.verification_status === 'pending' && (
            <p className="mt-4 text-xs text-foreground-400 flex items-center gap-1.5">
              <i className="ri-information-line" />
              Votre pièce d'identité a bien été reçue. Votre adresse professionnelle sera activée
              une fois votre identité vérifiée par notre équipe.
            </p>
          )}

          <button
            type="button"
            onClick={handleDelete}
            className="mt-4 text-sm text-foreground-400 hover:text-accent-500 transition-colors whitespace-nowrap"
          >
            Supprimer cette adresse
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Type d'adresse */}
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-2">
              Type d'adresse
            </label>
            <div className="inline-flex p-1 rounded-full bg-background-100 border border-background-200/70">
              <button
                type="button"
                onClick={() => setType('personal')}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${
                  type === 'personal'
                    ? 'bg-primary-500 text-foreground-950'
                    : 'text-foreground-400 hover:text-foreground-50'
                }`}
              >
                Personnel
              </button>
              <button
                type="button"
                onClick={() => setType('professional')}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${
                  type === 'professional'
                    ? 'bg-primary-500 text-foreground-950'
                    : 'text-foreground-400 hover:text-foreground-50'
                }`}
              >
                Professionnel
              </button>
            </div>
            <p className="text-xs text-foreground-400 mt-2">
              {type === 'professional'
                ? "L'adresse professionnelle nécessite la vérification de votre identité (carte d'électeur, CNI ou passeport)."
                : 'Aucune vérification requise pour une adresse personnelle.'}
            </p>
          </div>

          {/* Identité */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-foreground-400 mb-1.5">
                Prénom
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Votre prénom"
                className="w-full px-3.5 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-50 placeholder-foreground-600/50 focus:outline-none focus:border-primary-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-400 mb-1.5">
                Nom
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Votre nom"
                className="w-full px-3.5 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-50 placeholder-foreground-600/50 focus:outline-none focus:border-primary-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-400 mb-1.5">
                Pays
              </label>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-50 focus:outline-none focus:border-primary-400"
              >
                <option value="">Sélectionnez un pays</option>
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-400 mb-1.5">
                Numéro de téléphone
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+228 90 00 00 00"
                className="w-full px-3.5 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-50 placeholder-foreground-600/50 focus:outline-none focus:border-primary-400"
              />
            </div>
          </div>

          {/* Icône */}
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">
              Icône (photo ou logo)
            </label>
            <div className="flex items-center gap-3">
              {iconUrl ? (
                <img
                  src={iconUrl}
                  alt="Icône"
                  className="w-12 h-12 rounded-full object-cover border border-background-200/70"
                />
              ) : (
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-background-100 border border-background-200/70">
                  <i className="ri-camera-line text-xl text-foreground-500" />
                </div>
              )}
              <button
                type="button"
                onClick={() => iconInputRef.current?.click()}
                disabled={iconUploading}
                className="px-4 py-2.5 rounded-lg border border-background-300/60 text-foreground-50 text-sm font-semibold hover:bg-background-200/60 transition-colors disabled:opacity-60 whitespace-nowrap"
              >
                {iconUploading ? 'Téléversement…' : iconUrl ? "Changer l'icône" : 'Ajouter une icône'}
              </button>
              {iconUrl && (
                <button
                  type="button"
                  onClick={() => setIconUrl('')}
                  className="text-sm text-foreground-400 hover:text-accent-500 transition-colors whitespace-nowrap"
                >
                  Retirer
                </button>
              )}
              <input
                ref={iconInputRef}
                type="file"
                accept="image/*"
                onChange={handleIconUpload}
                className="hidden"
              />
            </div>
          </div>

          {/* Pièce d'identité */}
          {type === 'professional' && (
            <div className="p-4 rounded-lg bg-background-100 border border-background-200/70">
              <label className="block text-sm font-medium text-foreground-400 mb-2">
                Vérification d'identité
              </label>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-3">
                <select
                  value={docType}
                  onChange={(e) => setDocType(e.target.value)}
                  className="w-full sm:w-auto px-3.5 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-50 focus:outline-none focus:border-primary-400"
                >
                  {DOC_TYPES.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => docInputRef.current?.click()}
                  disabled={docUploading}
                  className="px-4 py-2.5 rounded-lg border border-background-300/60 text-foreground-50 text-sm font-semibold hover:bg-background-200/60 transition-colors disabled:opacity-60 whitespace-nowrap"
                >
                  {docUploading ? 'Téléversement…' : docUrl ? 'Changer le document' : 'Téléverser le document'}
                </button>
                <input
                  ref={docInputRef}
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleDocUpload}
                  className="hidden"
                />
              </div>
              {docUrl && (
                <p className="text-xs text-green-600 flex items-center gap-1.5">
                  <i className="ri-check-line" />
                  Document reçu. Il sera vérifié par notre équipe.
                </p>
              )}
            </div>
          )}

          {/* Adresse */}
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-2">
              Choisissez votre adresse
            </label>
            <div className="flex items-stretch gap-0">
              <input
                type="text"
                value={localPart}
                onChange={(e) => setLocalPart(sanitizeLocalPart(e.target.value))}
                placeholder={
                  profile?.full_name?.toLowerCase().replace(/\s+/g, '.') || 'votre.nom'
                }
                className="flex-1 min-w-0 px-4 py-3 bg-background-50 border border-background-200/70 rounded-l-lg text-sm text-foreground-50 placeholder-foreground-600/50 focus:outline-none focus:border-primary-400"
              />
              <span className="px-3 py-3 bg-background-100 border border-l-0 border-background-200/70 rounded-r-lg text-sm text-foreground-500 whitespace-nowrap flex items-center">
                @{EMAIL_DOMAIN}
              </span>
            </div>
            <p className="text-xs text-foreground-400 mt-2">
              Lettres, chiffres, points, tirets et underscores. 3 à 30 caractères.
            </p>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="px-5 py-3 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap text-sm"
          >
            {submitting ? 'Création…' : 'Créer mon adresse'}
          </button>
        </form>
      )}
    </div>
  );
}

export default EmailSection;